/**
 * Image detection utility with multi-tier detection strategy
 *
 * Strategy: Three-tier detection approach for optimal performance
 * 1. Check cache first (fastest - 0ms)
 * 2. Check Content-Type header and extension (no download required - 1-2ms)
 * 3. Read only first 16 bytes for magic byte detection (if file < 5MB - 5-10ms)
 */

import type { ImageMagicBytesSignature } from '@/constants/imageDetection.const';
import {
  EXTENSION_MIME_TYPES,
  IMAGE_DETECTION_CONFIG,
  IMAGE_MAGIC_BYTES,
  MIN_BYTES_FOR_DETECTION,
} from '@/constants/imageDetection.const';

/**
 * Result of image detection
 */
export interface ImageDetectionResult {
  /** Whether the file is an image */
  isImage: boolean;
  /** MIME type if detected */
  mimeType: string | null;
  /** Source of detection (cache, header, extension, magic-bytes, or none) */
  source: 'cache' | 'header' | 'extension' | 'magic-bytes' | 'none';
}

/**
 * Cache for detected image MIME types
 * Key: file name or URL
 * Value: { mimeType: string, timestamp: number }
 */
const imageTypeCache = new Map<
  string,
  { mimeType: string; timestamp: number }
>();

/**
 * Type guard to check if a value is a blob URL
 *
 * @param value - Value to check
 * @returns True if value is a blob URL string
 */
export function isBlobUrl(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('blob:');
}

/**
 * Check if a MIME type is an image type
 *
 * @param mimeType - MIME type to check
 * @returns True if MIME type represents an image
 */
export function isImageMimeType(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

/**
 * Check if byte sequence matches signature
 *
 * @param bytes - Byte array to check
 * @param signature - Signature definition
 * @returns True if bytes match signature
 */
export function matchesSignature(
  bytes: Uint8Array,
  signature: ImageMagicBytesSignature
): boolean {
  const { signature: sig, offset, validate } = signature;

  // Check if we have enough bytes
  if (bytes.length < offset + sig.length) {
    return false;
  }

  // Check signature bytes
  for (let i = 0; i < sig.length; i++) {
    if (bytes[offset + i] !== sig[i]) {
      return false;
    }
  }

  // Run additional validation if provided
  if (validate && !validate(bytes)) {
    return false;
  }

  return true;
}

/**
 * Detect image MIME type from magic bytes
 *
 * Magic bytes are unique byte sequences at the beginning of files
 * that reliably identify the file format, regardless of file extension
 * or HTTP Content-Type headers.
 *
 * This is essential for S3 files that may have:
 * - No file extension (e.g., evidence/#risk#domain#issue)
 * - Generic content-type (application/octet-stream)
 * - Incorrect content-type due to upload issues
 *
 * @param bytes - First 16+ bytes of the file
 * @returns MIME type string if detected, null otherwise
 *
 * @example
 * const bytes = new Uint8Array([0x89, 0x50, 0x4E, 0x47, ...]);
 * const mimeType = detectImageMimeTypeFromBytes(bytes);
 * // Returns: 'image/png'
 */
export function detectImageMimeTypeFromBytes(bytes: Uint8Array): string | null {
  if (bytes.length < MIN_BYTES_FOR_DETECTION) {
    return null;
  }

  // Check each signature in order
  for (const signature of IMAGE_MAGIC_BYTES) {
    if (matchesSignature(bytes, signature)) {
      return signature.mimeType;
    }
  }

  return null;
}

/**
 * Read only first N bytes from response without loading entire file
 *
 * Uses ReadableStream API to read only the first chunk of bytes,
 * significantly improving performance for large files.
 *
 * @param response - Fetch response object
 * @param length - Number of bytes to read
 * @returns Uint8Array containing first N bytes
 */
export async function readFirstBytes(
  response: Response,
  length: number
): Promise<Uint8Array> {
  // Clone response to avoid consuming original stream
  const reader = response.clone().body?.getReader();

  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const chunks: Uint8Array[] = [];
  let bytesRead = 0;

  try {
    while (bytesRead < length) {
      const { done, value } = await reader.read();

      if (done) break;

      chunks.push(value);
      bytesRead += value.length;

      // Stop reading after we have enough bytes
      if (bytesRead >= length) {
        await reader.cancel(); // Stop reading remaining data
        break;
      }
    }
  } finally {
    reader.releaseLock();
  }

  // Combine chunks into single Uint8Array
  const combined = new Uint8Array(bytesRead);
  let offset = 0;
  for (const chunk of chunks) {
    combined.set(chunk, offset);
    offset += chunk.length;
  }

  return combined.slice(0, length);
}

/**
 * Detect image type with multi-tier strategy
 *
 * Performance hierarchy:
 * - Cache: 0ms (instant return)
 * - Header/extension: 1-2ms (no download)
 * - Magic bytes: 5-10ms (only 16 bytes read vs full file)
 * - Large files: Skipped entirely (>5MB)
 *
 * @param fileResponse - Fetch response object
 * @param fileName - Optional file name for extension detection
 * @param fileUrl - Optional file URL for caching
 * @returns Detection result with MIME type and source
 */
export async function detectImageType(
  fileResponse: Response,
  fileName?: string,
  fileUrl?: string
): Promise<ImageDetectionResult> {
  const cacheKey = fileName || fileUrl || fileResponse.url;

  // Tier 1: Check cache
  const cached = imageTypeCache.get(cacheKey);
  if (
    cached &&
    Date.now() - cached.timestamp < IMAGE_DETECTION_CONFIG.CACHE_TTL_MS
  ) {
    return {
      isImage: true,
      mimeType: cached.mimeType,
      source: 'cache',
    };
  }

  // Tier 2: Check Content-Type header
  const contentType = fileResponse.headers.get('content-type') || '';
  if (contentType.startsWith('image/')) {
    const mimeType = contentType;
    imageTypeCache.set(cacheKey, { mimeType, timestamp: Date.now() });
    return { isImage: true, mimeType, source: 'header' };
  }

  // Tier 3: Check file extension
  if (fileName) {
    const extension = fileName.toLowerCase().split('.').pop() || '';

    if (extension in EXTENSION_MIME_TYPES) {
      const mimeType = EXTENSION_MIME_TYPES[extension];
      imageTypeCache.set(cacheKey, { mimeType, timestamp: Date.now() });
      return { isImage: true, mimeType, source: 'extension' };
    }
  }

  // Tier 4: Check file size before attempting magic byte detection
  const contentLength = fileResponse.headers.get('content-length');
  if (contentLength) {
    const size = parseInt(contentLength, 10);
    if (size > IMAGE_DETECTION_CONFIG.MAX_SIZE_FOR_MAGIC_BYTES) {
      // Skip magic bytes for large files
      return { isImage: false, mimeType: null, source: 'none' };
    }
  }

  // Tier 5: Read ONLY first 16 bytes for magic byte detection
  try {
    const bytes = await readFirstBytes(
      fileResponse,
      IMAGE_DETECTION_CONFIG.MAGIC_BYTE_READ_LENGTH
    );
    const detectedMimeType = detectImageMimeTypeFromBytes(bytes);

    if (detectedMimeType) {
      imageTypeCache.set(cacheKey, {
        mimeType: detectedMimeType,
        timestamp: Date.now(),
      });
      return {
        isImage: true,
        mimeType: detectedMimeType,
        source: 'magic-bytes',
      };
    }
  } catch (error) {
    console.error('Failed to detect image type from magic bytes:', error);
  }

  return { isImage: false, mimeType: null, source: 'none' };
}

/**
 * Detect image type with comprehensive error handling
 *
 * Safe wrapper around detectImageType that never throws.
 * Returns a safe fallback on any error.
 *
 * @param fileResponse - Fetch response object
 * @param fileName - Optional file name
 * @param fileUrl - Optional file URL
 * @returns Detection result (always returns, never throws)
 */
export async function detectImageTypeSafe(
  fileResponse: Response,
  fileName?: string,
  fileUrl?: string
): Promise<ImageDetectionResult> {
  try {
    return await detectImageType(fileResponse, fileName, fileUrl);
  } catch (error) {
    // Log error for monitoring but don't throw
    console.error('Image detection failed gracefully:', {
      error,
      fileName,
      fileUrl,
      status: fileResponse.status,
      statusText: fileResponse.statusText,
    });

    // Return safe fallback
    return {
      isImage: false,
      mimeType: null,
      source: 'none',
    };
  }
}
